import { AxiosResponse } from 'axios';
import { isArray } from 'lodash';
import { Dispatch } from 'redux';
import { FORM_AUTH_TYPE, FORM_SERVICE_TYPE, FULFILLED, OPERATION_VERB, SERVICE_MODE, SERVICE_TYPE_KEY, URL_PATH } from '../../constants';
import { IAuthDetailsUIModel, IEgressEventMappingModel, IEgressEventModel, IExceptionModel, IExceptionRequest, IHeaderParam, IIngressEventMappingModel, IIngressEventModel, IIngressExceptionRequestModel, INameSpaceParam, IQueryPathParam, IService, IServiceDetailsUIModel, IServiceDetailsValidationModel, IServiceOperation, IServiceOperationRequestModel, IServiceOperationValidationModel, ISoapAttributesRequest } from '../../models';
import { TranslateHook } from '../../modules/schema-generator/Utils';
import { addAllEgressEventMapping } from '../../store/reducer/services/EgressEventMappingReducer';
import { addAllIngressEventMapping } from '../../store/reducer/services/IngressEventMappingReducer';
import { serviceOperationsIncorrect } from '../../store/reducer/services/NavBarReducer';
import { ISaveAsDraftInitialState } from '../../store/reducer/services/SaveAsDraftReducer';
import { addServiceDetails } from '../../store/reducer/services/ServiceDetailsReducer';
import { addAllServiceOperations } from '../../store/reducer/services/ServiceOperationReducer';
import { objToArrForQueryAndHeader } from '../common/ObjectToArrayUtils';
import { sterilizeFunction } from '../common/SterilizeArrayUtil';
import { getHeaderAttribute, getModeFromUrl } from './ServiceListUtils';

/**
 * Checks service type is other that external
 * @param formData : service details from data
 * @returns true if not external else false
 */
export const isServiceTypeNotExternal = (formData: IServiceDetailsValidationModel) => formData.serviceType.value?.toLocaleLowerCase() !== FORM_SERVICE_TYPE.EXTERNAL;

/**
 * Check form is valid or not
 * @param formData : Service details form data
 * @returns true if invalid else false
 */

export const isServiceDetailsFormInvalid = (formData: IServiceDetailsValidationModel, checkClicked?: boolean): boolean => {
  const serviceTypes = [FORM_SERVICE_TYPE.REST, FORM_SERVICE_TYPE.SOAPSERVICE, FORM_SERVICE_TYPE.DB2, FORM_SERVICE_TYPE.MQ];
  const serviceTypesRestDb2Mq = [FORM_SERVICE_TYPE.REST, FORM_SERVICE_TYPE.DB2, FORM_SERVICE_TYPE.MQ];
  const serviceTypesRestMq = [FORM_SERVICE_TYPE.REST, FORM_SERVICE_TYPE.MQ];
  if ((!formData.serviceName.isValid && (getModeFromUrl() !== SERVICE_MODE.EDIT))
    || !formData.serviceName.value
    || !formData.endpoint.isValid
    || !formData.serviceDescription.isValid
    || !formData.authName.isValid
    || !formData.serviceType.value
    || ((serviceTypes.includes(formData.serviceType.value?.toLocaleLowerCase())) && !formData.endpoint.value)
    || ((serviceTypesRestMq.includes(formData.serviceType.value?.toLocaleLowerCase()))
      && checkClicked
      && !formData.authType.value)
    || (formData.serviceType.value?.toLocaleLowerCase() === FORM_SERVICE_TYPE.SOAPSERVICE && (formData.headerXml.value && !formData.headerXml.isValid))
    || ((serviceTypesRestDb2Mq.includes(formData.serviceType.value?.toLocaleLowerCase()))
      && ((formData.authType.value?.toLocaleLowerCase() === FORM_AUTH_TYPE.BASIC
        && (!formData.userName.isValid || !formData.userName.value || !formData.password.isValid || !formData.password.value))
        || (formData.authType.value?.toLocaleLowerCase() === FORM_AUTH_TYPE.OAUTH && ((!formData.authMethod.value || !formData.authUrl.value || !formData.authUrl.isValid)))))) {
    return true;
  }
  return false;
};

interface LooseObject {
   
  [key: string]: any
}
/**
 * @method convertArrayToObject
 * @description converts the array into the object
 * @param paramsList params array
 * @returns Header param list object
 */
export const convertArrayToObject = (paramsList: (IQueryPathParam | IHeaderParam)[]) => {
  const obj: LooseObject = {};
  if (isArray(paramsList)) {
    paramsList?.forEach((param) => {
      obj[param.key] = param.value;
    });
  }
  return obj;
};

/**
 * @method addServiceDetailsToStore
 * @description adds service details to redux store
 * @param formData: service details form data
 * @param dispatch: dispatch function
 */
export const addServiceDetailsToStore = (formData: IServiceDetailsValidationModel, dispatch: Dispatch, checkClick?: boolean, headerParamList?: IQueryPathParam[], requestParamList?: IQueryPathParam[], queryParamList?: IQueryPathParam[], soapNamespaceList?: INameSpaceParam[]) => {
  const createServiceDetails: IServiceDetailsUIModel = {
    serviceName: formData.serviceName.value,
    serviceDescription: formData.serviceDescription.value,
    endPoint: formData.endpoint.value,
    authName: formData.authName.value,
    serviceType: formData.serviceType.value,
    checkClicked: checkClick,
    authDetails: {
      authType: formData.authType.value,
      username: formData.userName.value,
      password: formData.password.value,
      authMethod: formData.authMethod.value,
      authUrl: formData.authUrl.value,
      headerParam: headerParamList?.length ? convertArrayToObject(headerParamList) as IQueryPathParam[] : {} as IQueryPathParam[],
      requestParam: requestParamList?.length ? convertArrayToObject(requestParamList) as IQueryPathParam[] : {} as IQueryPathParam[],
      queryPathParam: queryParamList?.length ? convertArrayToObject(queryParamList) as IQueryPathParam[] : {} as IQueryPathParam[],
    },
    soapAttributes: {
      headersXML: formData.headerXml.value,
      envelopeNamespaces: soapNamespaceList,
    }
  };
  dispatch(addServiceDetails(createServiceDetails));
};

export const isRestDb2MqService = (serviceType: string) => {
  const serviceTypes = [FORM_SERVICE_TYPE.REST, FORM_SERVICE_TYPE.DB2, FORM_SERVICE_TYPE.MQ];
  if (serviceTypes.includes(serviceType?.toLocaleLowerCase())) {
    return true;
  }
  return false;
};
/**
 * Checks if operation URL and operation verb is valid based on service type
 * @param serviceType: service type in service details form
 * @param serviceOperations: service operation data
 * @returns true if valid else false
 */
export const checkValidationOnServiceType = (serviceType: string, serviceOperations: IServiceOperation[]) => {
  if (isRestDb2MqService(serviceType)) {
    const serviceOpsArray = serviceOperations.filter((item) => !item.operationUrl
      || !item.operationVerb);
    if (serviceOpsArray.length) {
      return false;
    }
    return true;
  }
  if (serviceType?.toLocaleLowerCase() === FORM_SERVICE_TYPE.SOAPSERVICE) {
    const serviceOpsArray = serviceOperations.filter((item) => item.operationVerb !== OPERATION_VERB[0].name);
    if (serviceOpsArray.length) {
      return false;
    }
    return true;
  }
  return true;
};

/**
 *@method updatedServiceList
 * @param updatedAuth: takes updatedAuth from serviceList data
 * @returns clears updatedAuth section
 */
export const updatedServiceList = (updatedAuth: IAuthDetailsUIModel) => {
  updatedAuth.authType = '';
  updatedAuth.username = '';
  updatedAuth.password = '';
  updatedAuth.authMethod = '';
  updatedAuth.authUrl = '';
  updatedAuth.headerParam = [];
  updatedAuth.queryPathParam = [];
  updatedAuth.requestParam = [];
  return updatedAuth;
};

/**
 * Checks if ingress and egress event are present in service operation based on service type
 * @param serviceType: service type in service details form
 * @param serviceOperations: service operation data
 * @param ingressEventArray: ingress event added to the service operation
 * @param egressEventArray: egress event added to the service operation
 * @returns  true if valid else false
 */
export const checkValidationOnServiceEvents = (serviceType: string, serviceOperations: IServiceOperation[], ingressEventArray: IIngressEventMappingModel[], egressEventArray: IEgressEventMappingModel[]) => {
  const serviceTypes = [FORM_SERVICE_TYPE.REST, FORM_SERVICE_TYPE.SOAPSERVICE, FORM_SERVICE_TYPE.DB2, FORM_SERVICE_TYPE.MQ, FORM_SERVICE_TYPE.EXTERNAL];
  if (serviceTypes.includes(serviceType?.toLocaleLowerCase())) {
    return serviceOperations.every((item) => {
      const ingressEventCount = ingressEventArray.filter((event) => item.serviceOperation === event.serviceOperationName);
      return ingressEventCount.length;
    });
  }

  return serviceOperations.every((item) => {
    const egressEventCount = egressEventArray.filter((event) => item.serviceOperation === event.serviceOperationName);
    const ingressEventCount = ingressEventArray.filter((event) => item.serviceOperation === event.serviceOperationName);
    return egressEventCount.length && ingressEventCount.length;
  });
};

/**
 * @method getAuthDetailsModel
 * @description gets auth details model
 * @returns auth details model
 */
export const getAuthDetailsModel = (authDetails: IAuthDetailsUIModel | undefined) => {
  if (!authDetails || !authDetails.authType?.length) {
    return undefined;
  }
  const {
    authMethod = '',
    authType,
    authUrl = '',
    username = '',
    password = '',
    headerParam = [],
    queryPathParam = [],
    requestParam = [],
  } = authDetails;
  return {
    authMethod,
    authType,
    authUrl,
    username,
    password,
    headerParam,
    queryPathParam,
    requestParam,
  };
};

/**
 * @method getSoapAttributeModel
 * @description gets soap attribute model
 * @returns soap attribute model
 */
export const getSoapAttributeModel = (soapAttributes: ISoapAttributesRequest | undefined, clonedSaveAsDraftData: ISaveAsDraftInitialState) => {
  if (soapAttributes) {
    const envelopeNamespacesExist = soapAttributes?.envelopeNamespaces ? soapAttributes.envelopeNamespaces : [];
    const headersXMLExist = soapAttributes?.headersXML ? soapAttributes.headersXML : '';
    return {
      envelopeNamespaces: clonedSaveAsDraftData.serviceData.soapAttributes?.envelopeNamespaces !== undefined ? envelopeNamespacesExist : undefined,
      headersXML: clonedSaveAsDraftData.serviceData.soapAttributes?.headersXML !== undefined ? headersXMLExist : undefined,
    };
  }
  return undefined;
};

/**
 * @method sortException
 * @description Sorts the exception for comparison
 * @param exceptionArray exceptions
 * @returns sorted exceptions
 */
export const sortException = (exceptionArray: IExceptionModel[] | undefined) => {
  if (exceptionArray) {
    const sortedArray: IExceptionModel[] = [...exceptionArray].sort((n1, n2) => {
      if (n1.exceptionId > n2.exceptionId) {
        return 1;
      }
      if (n1.exceptionId < n2.exceptionId) {
        return -1;
      }
      return 0;
    });
    return sortedArray;
  }
  return [];
};

/**
 * @method sortEvents
 * @description Sorts the events for comparison
 * @param event ingress/egress events
 * @returns sorted events
 */
export const sortEvents = (events: (IIngressEventMappingModel | IEgressEventMappingModel)[]) => {
  if (events) {
    const sortedArray: (IIngressEventMappingModel | IEgressEventMappingModel)[] = [...events]?.sort((n1, n2) => {
      if (n1.eventName > n2.eventName) {
        return 1;
      }
      if (n1.eventName < n2.eventName) {
        return -1;
      }
      return 0;
    });
    return sortedArray;
  }
  return [];
};

/**
 * @method sortServiceOperation
 * @description Sorts the service operations for comparison
 * @param serviceOperations service operation
 * @returns sorted service operations
 */
export const sortServiceOperation = (serviceOperations: IServiceOperation[]) => {
  const sortedArray: IServiceOperation[] = [...serviceOperations]?.sort((n1, n2) => {
    if (n1.serviceOperation > n2.serviceOperation) {
      return 1;
    }
    if (n1.serviceOperation < n2.serviceOperation) {
      return -1;
    }
    return 0;
  });
  return sortedArray;
};

/**
 * @method updateOperationVerb
 * @description provides updated operation verb
 * @param prevServiceType
 * @param nextServiceType
 * @param currentOperationVerb
 */
const updateOperationVerb = (
  prevServiceType: string,
  nextServiceType: string,
  currentOperationVerb: string
) => {
  if (prevServiceType === FORM_SERVICE_TYPE.REST && (nextServiceType === FORM_SERVICE_TYPE.EXTERNAL || FORM_SERVICE_TYPE.SOAPSERVICE) && currentOperationVerb === 'PATCH') {
    return '';
  }
  if (prevServiceType === FORM_SERVICE_TYPE.REST && (nextServiceType === FORM_SERVICE_TYPE.EXTERNAL || FORM_SERVICE_TYPE.SOAPSERVICE)) {
    return currentOperationVerb;
  }
  if (nextServiceType.toLocaleLowerCase() === FORM_SERVICE_TYPE.SOAPSERVICE) {
    return 'POST';
  }
  return '';
};

/**
 * @method updateServiceOperationsOnServiceTypeChange
 * @description Updates the service operation on service type change
 * @param serviceOperationList Service Operation list
 * @param ingressEventList ingress event mapping list
 * @param egressEventList Egress event mapping list
 * @param dispatch dispatch the data to the redux store
 */
export const updateServiceOperationsOnServiceTypeChange = (
  serviceOperationList: IServiceOperation[],
  ingressEventList: IIngressEventMappingModel[],
  egressEventList: IEgressEventMappingModel[],
  dispatch: Dispatch,
  prevServiceType: string,
  nextServiceType: string
) => {
  const serviceTypes = [FORM_SERVICE_TYPE.SOAPSERVICE, FORM_SERVICE_TYPE.EXTERNAL];
  if (serviceTypes.includes(prevServiceType?.toLocaleLowerCase()) && serviceTypes.includes(nextServiceType?.toLocaleLowerCase())) {
    return;
  }

  if (serviceOperationList.length) {
    const updatedServiceOperation = serviceOperationList.map((serviceOperation) => {
      const newServiceOperation = { ...serviceOperation };
      newServiceOperation.operationVerb = updateOperationVerb(prevServiceType.toLocaleLowerCase(), nextServiceType.toLocaleLowerCase(), newServiceOperation.operationVerb);
      newServiceOperation.operationUrl = '';
      newServiceOperation.queryPathParam = null;
      newServiceOperation.headerParam = null;
      newServiceOperation.requestAttributes = [];
      newServiceOperation.responseAttributes = [];
      return newServiceOperation;
    });
    dispatch(addAllServiceOperations(updatedServiceOperation));
    dispatch(serviceOperationsIncorrect());

    const updatedIngressEventList = ingressEventList.map((ingressEvent) => {
      const updatedIngressEvent = { ...ingressEvent };
      updatedIngressEvent.headerToRequestHeader = undefined;
      updatedIngressEvent.headerToRequestPayload = undefined;
      updatedIngressEvent.requestToAuthenticationPayload = undefined;
      updatedIngressEvent.mqRequestMapping = undefined;
      updatedIngressEvent.payloadToQueryPath = undefined;
      updatedIngressEvent.payloadToRequestHeader = undefined;
      updatedIngressEvent.reqAttribute = '';
      return updatedIngressEvent;
    });

    dispatch(addAllIngressEventMapping({ ingressEventMappingList: updatedIngressEventList }));

    const updatedEgressEventList = egressEventList.map((egressEvent) => {
      const updatedEgressEvent = { ...egressEvent };
      updatedEgressEvent.mqResAttribute = undefined;
      updatedEgressEvent.resAttribute = '';
      return updatedEgressEvent;
    });

    dispatch(addAllEgressEventMapping({ egressEventMappingList: updatedEgressEventList }));
  }
};

/**
 * @method isXmlValid
 * @description checks if given input is valid xml or not
 * @param xml XML
 * @returns if xml is valid returns true else false
 */
export const isXmlValid = (xml: string) => {
  const domParser = new DOMParser();
  const dom = domParser.parseFromString(xml, 'application/xml');
  const errorNode = dom.querySelector('parsererror');
  if (errorNode) {
    return false;
  }
  return true;
};

/**
 * @method validateXmlData
 * @description Validates the XML data in the given form data and returns a new form data object with updated validation information.
 * @param formData - The form data containing the XML data to be validated.
 * @param translate - The translation hook used for translating error messages.
 * @returns A new form data object with updated validation information.
 */
export const validateXmlData = (formData: IServiceDetailsValidationModel, translate: TranslateHook) => {
  const newFormData = structuredClone(formData);
  if (!formData.headerXml.value?.length || isXmlValid(formData.headerXml.value)) {
    newFormData.headerXml.isValid = true;
    newFormData.headerXml.error = '';
  } else {
    newFormData.headerXml.isValid = false;
    newFormData.headerXml.isTouched = true;
    newFormData.headerXml.error = String(translate('service_validation_error.invalidXML'));
  }
  return newFormData;
};

/**
 * @method setUsernamePassWordEmpty
 * @description Sets the username, password, and authUrl fields in the formData object to empty values and marks them as valid.
 * @param formData - The IServiceDetailsValidationModel object containing the form data.
 */
export const setUsernamePassWordEmpty = (formData: IServiceDetailsValidationModel) => {
  formData.userName.value = '';
  formData.userName.isValid = true;
  formData.password.value = '';
  formData.password.isValid = true;
  formData.authUrl.value = '';
  formData.authUrl.isValid = true;
};

/**
 * @method getClonedDraftServiceDetails
 * @description Construct service object for comparison
 * @param serviceDetailsData Service details
 * @param serviceOperations Service OOperations
 * @param exceptions Service Exceptions
 * @param ingressEventMappingList Ingress event list
 * @param egressEventMappingList Egress event list
 * @returns Service Details object
 */
export const getClonedDraftServiceDetails = (
  serviceDetailsData: IServiceDetailsUIModel,
  serviceOperations: IServiceOperation[],
  exceptions: IExceptionModel[],
  ingressEventMappingList: IIngressEventMappingModel[],
  egressEventMappingList: IEgressEventMappingModel[]
) => {
  const clonedModel: IServiceDetailsUIModel = {
    serviceName: serviceDetailsData.serviceName,
    serviceType: serviceDetailsData.serviceType,
    serviceDescription: serviceDetailsData.serviceDescription,
    endPoint: serviceDetailsData.endPoint,
    checkClicked: serviceDetailsData.checkClicked,
    authDetails: getAuthDetailsModel(serviceDetailsData.authDetails),
    soapAttributes: serviceDetailsData.soapAttributes,
    importStatus: serviceDetailsData.importStatus
  };

  const clonedSaveAsDraftData: ISaveAsDraftInitialState = {
    serviceData: clonedModel,
    serviceOperations: sortServiceOperation(serviceOperations),
    serviceExceptions: sortException(exceptions),
    ingressEvent: sortEvents(ingressEventMappingList) as IIngressEventMappingModel[],
    egressEvent: sortEvents(egressEventMappingList) as IEgressEventMappingModel[]
  };

  return clonedSaveAsDraftData;
};

/**
 * @method getAuthDetails
 * @description Gets the decrypted auth details
 * @param header : auth details response
 * @returns  Auth Details
 */
const getAuthDetails = (authDetails: PromiseSettledResult<AxiosResponse<any, any> | null>, activeService: IService) => (authDetails?.status === FULFILLED ? authDetails.value?.data as IAuthDetailsUIModel : activeService?.authDetails);

/**
 * @method constructServiceDetailsForStore
 * @description constructs the service details object to store data into the redux store
 * @param activeService current active service
 * @param data axios response
 * @returns service details object
 */
export const constructServiceDetailsForStore = (activeService: IService, data?: AxiosResponse<any, any>) => {
  const serviceDetailsToStore: IServiceDetailsUIModel = {
    serviceName: activeService.serviceName,
    endPoint: activeService.endPoint,
    serviceType: activeService.serviceType?.toLocaleLowerCase() === SERVICE_TYPE_KEY.EXTERNAL?.toLocaleLowerCase() ? SERVICE_TYPE_KEY.EXTERNAL : activeService.serviceType,
    serviceDescription: activeService.serviceDescription as string,
    checkClicked: !!activeService?.authDetails?.authType,
    authDetails: {
      authType: activeService?.authDetails?.authType,
      username: activeService?.authDetails?.username,
      password: activeService?.authDetails?.password,
      authMethod: activeService?.authDetails?.authMethod,
      authUrl: activeService?.authDetails?.authUrl,
      headerParam: activeService?.authDetails?.headerParam,
      requestParam: activeService?.authDetails?.requestParam,
      queryPathParam: activeService?.authDetails?.queryPathParam,
    },
    soapAttributes: {
      headersXML: activeService?.soapAttributes?.headersXML?.length ? data?.data?.headerAttribute : '',
      envelopeNamespaces: activeService?.soapAttributes?.envelopeNamespaces ? activeService?.soapAttributes?.envelopeNamespaces : [],
    },
  };

  return serviceDetailsToStore;
};

/**
 * @method getEditServiceURLArray
 * @description Gets the edit service url array
 * @param serviceNameFromURL service name from url
 * @param status service status
 * @returns edit service url array
 */
export const getEditServiceURLArray = (serviceNameFromURL: string, version: string) => {
  const buildEditPath = (base: string) =>
    [URL_PATH.SERVICE_HOME + base, serviceNameFromURL, version, SERVICE_MODE.EDIT].filter(Boolean).join('/');
  return [
    buildEditPath(URL_PATH.VIEW_SERVICE),
    buildEditPath(URL_PATH.VIEW_EXCEPTION),
    buildEditPath(URL_PATH.VIEW_SERVICE_OPERATION)
  ];
};

/**
 * @method getCreateServiceURLArray
 * @description Gets the create service url array
 * @returns create service url array
 */
export const getCreateServiceURLArray = () => ([
  `${URL_PATH.SERVICE_HOME}${URL_PATH.CREATE_SERVICE}`,
  `${URL_PATH.SERVICE_HOME}${URL_PATH.CREATE_EXCEPTION}`,
  `${URL_PATH.SERVICE_HOME}${URL_PATH.CREATE_SERVICE_OPERATION}`
]);

/**
 * @method resetServiceOperationFormOnCancelClick
 * @description resets the service operation form data
 * @param serviceOpsFormData service operation form data
 * @returns reset model
 */
export const resetServiceOperationFormOnCancelClick = (serviceOpsFormData: IServiceOperationValidationModel) => {
  const newFormData = { ...serviceOpsFormData };
  newFormData.serviceOperationName.value = '';
  newFormData.serviceOperationName.isValid = true;
  newFormData.serviceOperationName.isTouched = false;
  newFormData.operationVerb.value = '';
  newFormData.operationURL.value = '';
  newFormData.operationURL.isValid = true;
  return newFormData;
};

/**
 * @method getServiceDetailsDataFromReducer
 * @description gets the service details form reducer
 * @param serviceDetailsInReducerData service reducer data
 * @returns reducer service details object
 */
export const getServiceDetailsDataFromReducer = (serviceDetailsInReducerData: IServiceDetailsUIModel) => {
  const {
    serviceName,
    endPoint,
    serviceType,
    serviceDescription,
    authDetails,
    soapAttributes,
  } = serviceDetailsInReducerData;
  const reducerData = {
    serviceName,
    serviceType,
    endPoint,
    serviceDescription,
    authDetails: {
      authType: authDetails?.authType,
      userName: authDetails?.username,
      password: authDetails?.password,
      authMethod: authDetails?.authMethod,
      authUrl: authDetails?.authUrl,
      headerParam: objToArrForQueryAndHeader(authDetails?.headerParam),
      queryPathParam: objToArrForQueryAndHeader(authDetails?.queryPathParam),
      requestParam: objToArrForQueryAndHeader(authDetails?.requestParam),
    },
    soapAttributes: {
      headerXml: soapAttributes?.headersXML,
      envelopNamespace: soapAttributes?.envelopeNamespaces,
    }
  };
  return reducerData;
};

/**
 * @method getServiceDetailsFromForm
 * @description gets the service details form form
 * @param formData form data
 * @param headerParamList header param list
 * @param queryPathParamList query param list
 * @param requestParamList request param list
 * @param soapNamespaceList soap namespace list
 * @returns service details data
 */
export const getServiceDetailsFromForm = (
  formData: IServiceDetailsValidationModel,
  headerParamList: IQueryPathParam[],
  queryPathParamList: IQueryPathParam[],
  requestParamList: IQueryPathParam[],
  soapNamespaceList: INameSpaceParam[] | undefined
) => {
  const dataInForm = {
    serviceName: formData.serviceName.value,
    serviceType: formData.serviceType.value,
    endPoint: formData.endpoint.value,
    serviceDescription: formData.serviceDescription.value,
    authDetails: {
      authType: formData.authType.value,
      userName: formData.userName.value,
      password: formData.password.value,
      authMethod: formData.authMethod.value,
      authUrl: formData.authUrl.value,
      headerParam: objToArrForQueryAndHeader(headerParamList),
      queryPathParam: objToArrForQueryAndHeader(queryPathParamList),
      requestParam: objToArrForQueryAndHeader(requestParamList)
    },
    soapAttributes: {
      headerXml: formData.headerXml.value,
      envelopNamespace: soapNamespaceList
    }
  };

  return dataInForm;
};

/**
 * @method getConvertedHeader
 * @description gets converted header params
 * @param authDetails auth details
 * @param serviceAuthDetails service auth details
 * @returns converted object array
 */
export const getConvertedHeader = (authDetails: IAuthDetailsUIModel | undefined, serviceAuthDetails: IAuthDetailsUIModel | undefined) => {
  if (authDetails?.headerParam) {
    return objToArrForQueryAndHeader(authDetails?.headerParam);
  }
  return objToArrForQueryAndHeader(serviceAuthDetails?.headerParam);
};

/**
 * @method getConvertedQueryParam
 * @description gets converted query params
 * @param authDetails auth details
 * @param serviceAuthDetails service auth details
 * @returns converted object array
 */
export const getConvertedQueryParam = (authDetails: IAuthDetailsUIModel | undefined, serviceAuthDetails: IAuthDetailsUIModel | undefined) => {
  if (authDetails?.queryPathParam) {
    return objToArrForQueryAndHeader(authDetails?.queryPathParam);
  }
  return objToArrForQueryAndHeader(serviceAuthDetails?.queryPathParam);
};

/**
 * @method getConvertedRequestParam
 * @description gets converted request params
 * @param authDetails auth details
 * @param serviceAuthDetails service auth details
 * @returns converted object array
 */
export const getConvertedRequestParam = (authDetails: IAuthDetailsUIModel | undefined, serviceAuthDetails: IAuthDetailsUIModel | undefined) => {
  if (authDetails?.requestParam) {
    return objToArrForQueryAndHeader(authDetails?.requestParam);
  }
  return objToArrForQueryAndHeader(serviceAuthDetails?.requestParam);
};

/**
 * @method getCreateViewServiceTitle
 * @description Gets the title for service based on create/edit/view mode
 * @param serviceMode service mode
 * @param isEdit service edit
 * @returns true if edit mode, else false
 */
export const getCreateViewServiceTitle = (serviceMode: string, isEdit: boolean) => {
  const createOrViewTitle = 'service_wrapper_component.wrapper_title';
  if (isEdit) {
    return '';
  }
  return (serviceMode === SERVICE_MODE.VIEW || serviceMode === SERVICE_MODE.EDIT) ? '' : createOrViewTitle;
};

/**
 * Gets the schema for Events
 * @param isSchemaLoading boolean
 * @param schema event schema
 * @param viewSchemaClicked boolean
 * @param schemaJson schema state
 * @returns schema or error as payload
 */
export const getSchemaForEvent = (isSchemaLoading: boolean, schema: string | undefined, viewSchemaClicked: boolean, schemaJson: { payload: string, error: string }) => {
  if (!isSchemaLoading && schema && viewSchemaClicked) {
    let schemaData = {
      ...schemaJson
    };
    try {
      const parsedJson = JSON.parse(schema);
      if (parsedJson === null) throw new TypeError('null string');
      schemaData = {
        ...schemaData,
        payload: schema
      };
    } catch (err) {
      schemaData = {
        ...schemaData,
        error: 'error'
      };
    }
    return schemaData;
  }
  return { payload: '', error: '' };
};

/**
 * Gets Service Operations for redux store when service viewed
 * @param serviceOperationList Service Operations list from api
 */
export const getServiceOperationListForUI = (serviceOperationList: IServiceOperationRequestModel[] | undefined) => {
  if (serviceOperationList) {
    const serviceOperations: IServiceOperation[] = serviceOperationList?.map((serviceOperation) => {
      const newServiceOperation: IServiceOperation = {
        serviceOperation: serviceOperation.serviceOperation,
        operationVerb: serviceOperation.operationVerb ?? '',
        operationUrl: serviceOperation.operationUrl ?? '',
        headerParam: serviceOperation.headerParam && Object.keys(serviceOperation.headerParam as object)?.length ? objToArrForQueryAndHeader(serviceOperation.headerParam) : [],
        queryPathParam: serviceOperation.queryPathParam && Object.keys(serviceOperation.queryPathParam as object)?.length ? objToArrForQueryAndHeader(serviceOperation.queryPathParam) : [],
        requestAttributes: serviceOperation.requestAttributes,
        responseAttributes: serviceOperation.responseAttributes,
        security: serviceOperation.security ?? '',
        retry: serviceOperation.retry,
        timeOut: serviceOperation.timeout !== undefined ? serviceOperation.timeout.toString() : undefined
      };
      return newServiceOperation;
    });
    return serviceOperations;
  }
  return [];
};

/**
 * Gets the exception list for redux store
 * @param serviceExceptions service exceptions from api
 * @returns
 */
export const getExceptionsForUI = (serviceExceptions: IExceptionRequest[] | undefined) => {
  if (serviceExceptions) {
    return serviceExceptions?.slice().reverse()?.map((item: IExceptionRequest, index: number) => {
      const exceptionModel: IExceptionModel = {
        id: index,
        exceptionId: item.ersExceptionName,
        errorCode: parseInt(item.errorCode, 10),
        action: item.actions,
        isEdit: false
      };
      return exceptionModel;
    });
  }
  return [];
};

/**
 * Gets Ingress events for redux store when service viewed
 * @param eventArray Ingress event array from api
 * @param serviceOperationName service operation name
 */
export const getIngressEventListForUI = (eventArray: IIngressEventModel[], serviceOperationName: string) => eventArray.map((ingressEvent) => {
  const sanitizedException = ingressEvent.ingressConfig?.exceptionMappings?.map((item: IIngressExceptionRequestModel) => {
    const exceptionObj = {
      action: sterilizeFunction(item.actions),
      exceptionId: item.ersExceptionName,
      errorCode: item.errorCode
    };
    return exceptionObj;
  });
  const { ingressEventName, ingressEventVersion } = ingressEvent;
  const ingressEventToAdd: IIngressEventMappingModel = {
    serviceOperationName,
    eventName: ingressEventName,
    eventVersion: `${ingressEventVersion}`,
    reqAttribute: ingressEvent.ingressConfig?.requestSpecJson ?? '',
    mqRequestMapping: ingressEvent.ingressConfig?.mqRequestSpec ?? '',
    payloadToQueryPath: ingressEvent.ingressConfig?.payloadToQueryPathSpecJson ?? undefined,
    payloadToRequestHeader: ingressEvent.ingressConfig?.payloadToRequestHeaderSpecJson ?? undefined,
    headerToRequestHeader: ingressEvent.ingressConfig?.headerToRequestHeaderSpecJson ?? undefined,
    headerToRequestPayload: ingressEvent.ingressConfig?.headerToRequestPayloadSpecJson ?? undefined,
    requestToAuthenticationPayload: ingressEvent.ingressConfig?.authSpecJson ?? undefined,
    exceptions: sanitizedException
  };
  return ingressEventToAdd;
});

/**
 * Gets Egress events for redux store when service viewed
 * @param eventArray Ingress event array from api
 * @param serviceOperationName service operation name
 */
export const getEgressEventListForUI = (eventArray: IEgressEventModel[], serviceOperationName: string, ingressName: string, ingressEventVersion: string) => eventArray.map((egressEvent) => {
  const { egressEventName, egressEventVersion } = egressEvent;
  let egressEventToAdd: IEgressEventMappingModel = {
    serviceOperationName,
    ingressName,
    ingressEventVersion,
    eventName: egressEventName,
    eventVersion: `${egressEventVersion}`,
    resAttribute: egressEvent.egressConfig?.responseSpecJson ?? '',
    mqResAttribute: egressEvent.egressConfig?.mqResponseSpec ?? '',
  };
  if (egressEvent.egressConfig?.externalTopic?.length) {
    const externalTopic = egressEvent.egressConfig?.externalTopic[0];
    egressEventToAdd = {
      ...egressEventToAdd,
      topic: externalTopic.topicName ?? '',
      partition: `${externalTopic.partitionId ?? ''}`,
      connection: externalTopic.connectionString ?? ''
    };
  }
  return egressEventToAdd;
});

// function to return class strings for sticky ternary expressions moveed from ServiceWrapper to Util to reduce complexity
export const getStickyStyle = (isSticky: boolean, ifClassString: string, orClassString: string) => (isSticky ? ifClassString : orClassString);

// function to get version in sticky header
export const getServiceVersion = (isEdit: boolean, viewStatus: boolean, serviceVersion: string | undefined) => (((isEdit || viewStatus) && serviceVersion) ? `Ver ${serviceVersion}` : '');
